import { useState } from 'react';
import { createReminder } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Calendar } from '../ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Clock, Mail, MessageSquare, Bell, Zap } from 'lucide-react';
import { format, addDays, setHours, setMinutes } from 'date-fns';
import { toast } from 'sonner';

export const ReminderModal = ({ open, onClose, lead, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(addDays(new Date(), 1));
  const [time, setTime] = useState('09:00');
  const [message, setMessage] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(false);

  const handleSubmit = async () => {
    const [hours, minutes] = time.split(':').map(Number);
    const scheduledAt = setMinutes(setHours(date, hours), minutes);
    
    if (scheduledAt <= new Date()) {
      toast.error('Please select a future date and time');
      return;
    }

    setLoading(true);
    try {
      await createReminder(lead.id, scheduledAt.toISOString(), message, notifyEmail, notifySms);
      toast.success('Reminder scheduled!');
      onCreated();
      onClose();
      // Reset form
      setDate(addDays(new Date(), 1));
      setTime('09:00');
      setMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSMS = async (daysFromNow, timeStr, quickMessage) => {
    const targetDate = addDays(new Date(), daysFromNow);
    const [hours, minutes] = timeStr.split(':').map(Number);
    const scheduledAt = setMinutes(setHours(targetDate, hours), minutes);
    
    setLoading(true);
    try {
      await createReminder(lead.id, scheduledAt.toISOString(), quickMessage, false, true);
      toast.success(`SMS reminder set for ${format(scheduledAt, 'MMM d, h:mm a')}`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create reminder');
    } finally {
      setLoading(false);
    }
  };

  const quickSet = (daysFromNow, timeStr) => {
    setDate(addDays(new Date(), daysFromNow));
    setTime(timeStr);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Bell className="w-5 h-5 text-accent" />
            Schedule Reminder
          </DialogTitle>
          <DialogDescription>
            Set a reminder to follow up with <strong>{lead?.business_name}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Quick SMS Reminders */}
          <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg space-y-2">
            <Label className="flex items-center gap-2 text-green-600">
              <Zap className="w-4 h-4" />
              Quick SMS Reminder
            </Label>
            <p className="text-xs text-muted-foreground">One-tap to text yourself a reminder</p>
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleQuickSMS(0, '14:00', `Follow up with ${lead?.business_name}`)}
                className="text-xs border-green-500/30 hover:bg-green-500/10"
                disabled={loading}
                data-testid="quick-sms-today"
              >
                <MessageSquare className="w-3 h-3 mr-1 text-green-500" />
                Today 2PM
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleQuickSMS(1, '09:00', `Follow up with ${lead?.business_name}`)}
                className="text-xs border-green-500/30 hover:bg-green-500/10"
                disabled={loading}
                data-testid="quick-sms-tomorrow"
              >
                <MessageSquare className="w-3 h-3 mr-1 text-green-500" />
                Tomorrow 9AM
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleQuickSMS(0, format(addDays(new Date(), 0).setHours(new Date().getHours() + 1), 'HH:00'), `Call ${lead?.business_name} NOW`)}
                className="text-xs border-green-500/30 hover:bg-green-500/10"
                disabled={loading}
                data-testid="quick-sms-1hour"
              >
                <MessageSquare className="w-3 h-3 mr-1 text-green-500" />
                In 1 Hour
              </Button>
            </div>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or schedule custom</span>
            </div>
          </div>

          {/* Calendar - Always Visible */}
          <div className="space-y-2">
            <Label>Pick a Date</Label>
            <div className="border rounded-lg p-2 flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(newDate) => newDate && setDate(newDate)}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                className="rounded-md"
                data-testid="reminder-calendar"
              />
            </div>
            <p className="text-xs text-center text-muted-foreground">
              Selected: <strong>{format(date, 'EEEE, MMMM d, yyyy')}</strong>
            </p>
          </div>

          {/* Time Picker */}
          <div className="space-y-2">
            <Label>Pick a Time</Label>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger data-testid="reminder-time-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {Array.from({ length: 48 }, (_, i) => {
                    const hour = Math.floor(i / 2);
                    const minute = i % 2 === 0 ? '00' : '30';
                    const hourStr = hour.toString().padStart(2, '0');
                    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                    const ampm = hour < 12 ? 'AM' : 'PM';
                    return (
                      <SelectItem key={`${hourStr}:${minute}`} value={`${hourStr}:${minute}`}>
                        {displayHour}:{minute} {ampm}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Quick Time Buttons */}
            <div className="flex flex-wrap gap-1">
              {['09:00', '10:00', '12:00', '14:00', '16:00', '18:00'].map(t => (
                <Button
                  key={t}
                  variant={time === t ? 'default' : 'outline'}
                  size="sm"
                  className="text-xs h-7 px-2"
                  onClick={() => setTime(t)}
                >
                  {parseInt(t) === 0 ? '12 AM' : parseInt(t) < 12 ? `${parseInt(t)} AM` : parseInt(t) === 12 ? '12 PM' : `${parseInt(t) - 12} PM`}
                </Button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label>Custom Note (optional)</Label>
            <Textarea
              placeholder="e.g., Discuss proposal pricing..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              data-testid="reminder-message-input"
            />
          </div>

          {/* Notification Methods */}
          <div className="space-y-3">
            <Label>Send Reminder Via</Label>
            <div className="grid grid-cols-2 gap-2">
              <div 
                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${notifyEmail ? 'bg-blue-500/20 border border-blue-500/40' : 'bg-muted/30 border border-transparent'}`}
                onClick={() => setNotifyEmail(!notifyEmail)}
              >
                <div className="flex items-center gap-2">
                  <Mail className={`w-4 h-4 ${notifyEmail ? 'text-blue-500' : 'text-muted-foreground'}`} />
                  <span className="text-sm">Email</span>
                </div>
                <Switch 
                  checked={notifyEmail} 
                  onCheckedChange={setNotifyEmail}
                  data-testid="notify-email-switch"
                />
              </div>
              <div 
                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${notifySms ? 'bg-green-500/20 border border-green-500/40' : 'bg-muted/30 border border-transparent'}`}
                onClick={() => setNotifySms(!notifySms)}
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className={`w-4 h-4 ${notifySms ? 'text-green-500' : 'text-muted-foreground'}`} />
                  <span className="text-sm">SMS</span>
                </div>
                <Switch 
                  checked={notifySms} 
                  onCheckedChange={setNotifySms}
                  data-testid="notify-sms-switch"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={loading || (!notifyEmail && !notifySms)}
              className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground"
              data-testid="create-reminder-btn"
            >
              {loading ? 'Scheduling...' : 'Schedule Reminder'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
